import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useGaiaWebsocket } from "@expert/gaia";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { sdkEventBus, useActiveTask, usePartner, useSessionId, type VoiceTask } from "@/sdk";

export const useSetGaiaSessionContext = () => {
    const gaiaWS = useGaiaWebsocket();
    const partner = usePartner();
    const sessionId = useSessionId();
    const { features } = useFeatures<WorkspaceFeatures>();
    const { data: realtimeAttributes } = useQuery({
        queryKey: ["realtime-attributes", sessionId, partner],
        queryFn: async () => getRealtimeAttributes(features),
        retry: 1,
    });
    const task = useActiveTask();
    const callSid = (task as VoiceTask)?.agentCallId;

    useEffect(() => {
        const sessionStartedSubscription = sdkEventBus.on("call_accepted", (task) => {
            if (realtimeAttributes && gaiaWS && !gaiaWS.loading) {
                void gaiaWS.sendJsonMessage({
                    action: "update-session-context",
                    correlationId: sessionId,
                    sessionId: task.agentCallId,
                    sessionGroupId: sessionId,
                    partner,
                    context: {
                        properties: [
                            {
                                field: "realtime_tools",
                                value: realtimeAttributes,
                                action: "add",
                            },
                        ],
                    },
                });
            }
        });

        return () => sessionStartedSubscription();
    }, [gaiaWS, partner, realtimeAttributes, sessionId]);

    useEffect(() => {
        if (task?.locale && gaiaWS && !gaiaWS.loading) {
            void gaiaWS.sendJsonMessage({
                action: "update-session-context",
                correlationId: sessionId,
                sessionId: callSid ?? sessionId,
                sessionGroupId: sessionId,
                partner,
                context: {
                    properties: [
                        {
                            field: "locale",
                            value: task.locale,
                            action: "add",
                        },
                    ],
                },
            });
        }
    }, [callSid, gaiaWS, partner, sessionId, task?.locale]);
};

export const getRealtimeAttributes = async (workspaceFeatures: WorkspaceFeatures) => {
    const {
        isRapportEnabled,
        isEnrollmentCelebrationEnabled,
        isEngagementEnabled,
        isGaiaMultiStepSummaryEnabled,
        isGaiaCallResolutionEnabled,
        isGuidedFlowRecommendationEnabled,
        isPersonalizeCoverageEnabled,
    } = workspaceFeatures;

    // TODO: replace "gaia-post-call-summary" with "gaia-multistep-call-summarization" when post-call-summary is deprecated on gaia side
    const realtimeTools = ["gaia-post-call-summary", "gaia-device-cues", "gaia-sales-checklist-mentions"];
    if (isEngagementEnabled) {
        realtimeTools.push("gaia-engagement-question-checklist");
    }
    if (isEnrollmentCelebrationEnabled) {
        realtimeTools.push("gaia-enrollment-celebration-checklist");
    }
    if (isGaiaCallResolutionEnabled) {
        realtimeTools.push("gaia-call-resolution");
    }
    if (isGaiaMultiStepSummaryEnabled) {
        realtimeTools.push("gaia-multistep-call-summarization");
    }
    if (isGuidedFlowRecommendationEnabled) {
        realtimeTools.push("gaia-call-category-cues");
    }
    if (isPersonalizeCoverageEnabled) {
        realtimeTools.push("gaia-example-of-coverage");
    }
    if (isRapportEnabled) {
        realtimeTools.push("gaia-rapport-checklist");
    }
    return realtimeTools.join(",");
};
